/**
 * Sidebar.tsx — the right-hand panel: metrics + balance sheet + legend.
 *
 * Three sections, top to bottom:
 *  1. Metric cards — the headline numbers (revenue, costs, fulfilment, ...),
 *     each with its direction arrow (▲ up = good green, ▼ down = good red).
 *     A "+ metric" placeholder card sits at the end of the grid; clicking it
 *     opens the GlobalMetricsEditModal. Double-clicking a card edits that
 *     metric. (The old "Edit metrics" expander is gone — the modal replaces it.)
 *  2. Balance sheet — a global two-column (in / out) ledger plus per-node
 *     rows, collapsible. This is the spec's "balance sheet is always two
 *     columns and this is what we are balancing."
 *  3. Legend — what each edge colour means, so the graph is legible.
 *
 * Numbers come from the useSim snapshot (already derived via the engine's
 * buildBalanceSheet + computeMetrics); the Sidebar is presentational only.
 */
import { useState, useEffect, useRef, useMemo } from 'react';
import { Plus, X, PencilSimple, DotsSixVertical, ArrowsDownUp } from '@phosphor-icons/react';

import type { BalanceSheet, GroupBalance } from '../engine/balance';
import type { CellBinding, DbKnobPin, KnobPin, LegendEntry, MetricSpec, ParamSpec, ScenarioBundle } from '../engine/types';
import { bindingLabel, lookupBoundCell } from '../engine/bindings';
import { graphLint } from '../engine/lint';
import { getDefaultStorage } from '../storage/LocalStorageBackend';
import { usePersistedState } from '../storage/usePersistedState';
import { flowLegend } from '../graph/palette';
import { GlobalMetricsEditModal } from './GlobalMetricsEditModal';
import { KnobsEditModal } from './KnobsEditModal';

/** Pref storage for the sidebar's persisted UI state (localStorage when
 *  available, memory fallback otherwise). */
const prefsStorage = getDefaultStorage();

/** One editable project table — mirrors the inline shape in `useSim.tablesState`.
 *  Defined here so the Knobs section can take it without importing the hook. */
export interface KnobTableSnapshot {
  name: string;
  columns: { name: string; type: string; required?: boolean; default?: unknown }[];
  rows: { id: number | string; cells: Record<string, string | number | boolean | null> }[];
}

interface SidebarProps {
  bundle: ScenarioBundle;
  balance: BalanceSheet;
  metrics: Record<string, number>;
  nodeLabel: (id: string) => string;
  selectedNodeId: string | null;
  onSelectNode: (id: string | null) => void;
  canEdit: boolean;
  onAddMetric?: (m: MetricSpec) => void;
  onUpdateMetric?: (m: MetricSpec) => void;
  onRemoveMetric?: (key: string) => void;
  onRenameMetric?: (oldKey: string, newKey: string) => void;
  /** Replace the bundle's whole legend list. Cosmetic; persisted per-project.
   *  When unset, the legend editor is read-only (still renders the entries). */
  onSetLegend?: (legend: LegendEntry[]) => void;
  /** Replace the bundle's whole pinned-knobs list. When unset, the Knobs
   *  section's edit button is hidden (read-only display still renders). */
  onSetKnobs?: (knobs: KnobPin[]) => void;
  /** Live param values keyed by setParam path (from the latest tick), used to
   *  render graph-knob sliders. Falls back to the bundle's initialParams. */
  paramValues?: Record<string, number>;
  /** Project tables snapshot — null in local mode (the db-knob create tab is
   *  hidden) and before the first load lands. */
  tablesState?: KnobTableSnapshot[] | null;
  /** Lazy-load the tables snapshot. Fired when a db knob exists but tables
   *  aren't loaded yet. */
  onLoadTables?: () => Promise<void>;
  /** PATCH a project table cell (db-knob slider commit). */
  onUpdateTableCell?: (table: string, rowId: number | string, column: string, value: number) => Promise<void>;
  /** Push a graph-knob slider edit to the engine (high-frequency; the engine
   *  reads the new value on the next tick). */
  onSetParam?: (path: string, value: number) => void;
  /** Per-group aggregated balance, keyed by group id. Empty when the bundle
   *  has no node groups. */
  groupBalance?: Record<string, GroupBalance>;
  /** Currently selected group id (null = none). Selecting a row opens the
   *  group modal in the App. */
  selectedGroupId?: string | null;
  onSelectGroup?: (id: string | null) => void;
  children?: React.ReactNode;
}

export function Sidebar({
  bundle,
  balance,
  metrics,
  nodeLabel,
  selectedNodeId,
  onSelectNode,
  canEdit,
  onAddMetric,
  onUpdateMetric,
  onRemoveMetric,
  onRenameMetric,
  onSetLegend,
  onSetKnobs,
  paramValues,
  tablesState,
  onLoadTables,
  onUpdateTableCell,
  onSetParam,
  groupBalance = {},
  selectedGroupId = null,
  onSelectGroup,
  children,
}: SidebarProps): JSX.Element {
  // Balance-sheet section collapse state — persisted so a reload keeps the
  // user's layout. Both default collapsed.
  const [nodesExpanded, setNodesExpanded] = usePersistedState(prefsStorage, 'sidebar.nodesExpanded', false);
  const [groupsExpanded, setGroupsExpanded] = usePersistedState(prefsStorage, 'sidebar.groupsExpanded', false);
  /** The metric being edited, or null for "create new", or undefined = closed. */
  const [editingMetric, setEditingMetric] = useState<MetricSpec | null | undefined>(undefined);
  /** Whether the pinned-knobs edit modal is open. */
  const [editingKnobs, setEditingKnobs] = useState(false);
  const customKinds = new Set<string>();
  for (const e of bundle.graph.edges) {
    if (e.flowKind === 'custom' && e.customKind) customKinds.add(e.customKind);
  }
  // The legend is per-project once edited (bundle.legend). Until the user
  // touches it, derive entries from the palette so colours stay legible —
  // matches the pre-persistence behaviour for untouched bundles.
  const legendEntries = bundle.legend ?? flowLegend(customKinds);

  // Save from the single-metric modal. Create vs. update + key rename are
  // bridged to the key-identity sim primitives.
  const saveMetric = (m: MetricSpec) => {
    const creating = editingMetric == null;
    if (creating) {
      onAddMetric?.(m);
    } else {
      const prev = editingMetric as MetricSpec;
      if (prev.key !== m.key) onRenameMetric?.(prev.key, m.key);
      onUpdateMetric?.(m);
    }
    setEditingMetric(undefined);
  };
  const deleteMetric = () => {
    if (editingMetric) onRemoveMetric?.((editingMetric as MetricSpec).key);
    setEditingMetric(undefined);
  };

  return (
    <aside className="sidebar">
      {children}
      {/* ─── Metrics ────────────────────────────────────────────────── */}
      <section className="sidebar-section">
        <div className="metric-grid">
          {bundle.metrics.map((spec) => (
            <MetricCard
              key={spec.key}
              spec={spec}
              value={metrics[spec.key]}
              editable={canEdit}
              onEdit={() => setEditingMetric(spec)}
            />
          ))}
          {/* "+ metric" placeholder card — last cell in the grid. Opens the
              single-metric modal in create mode. */}
          {canEdit && (
            <button
              type="button"
              className="metric-card metric-add"
              onClick={() => setEditingMetric(null)}
              title="Add metric"
            >
              <Plus size={16} weight="bold" />
              <span>metric</span>
            </button>
          )}
        </div>
      </section>

      {/* ─── Balance sheet ──────────────────────────────────────────── */}
      <section className="sidebar-section">
        <div className="balance-sheet">
          {/* The "All" total is the sum over every NODE only. Groups are
              spatial subsets of the node set, so a per-group row is a
              re-cut of the same numbers — it must NEVER be added into this
              global total (that would double-count every member node). */}
          <div className="balance-row balance-global">
            <span>All</span>
            <span className="num in">+{fmt(balance.global.inflow)}</span>
            <span className="num out">-{fmt(balance.global.outflow)}</span>
            <span className={`num net ${balance.global.net >= 0 ? 'pos' : 'neg'}`}>
              {balance.global.net >= 0 ? '+' : '-'}{fmt(Math.abs(balance.global.net))}
            </span>
          </div>
          <button
            type="button"
            className="balance-toggle"
            onClick={() => setNodesExpanded((x) => !x)}
          >
            {nodesExpanded ? '▼' : '▶'} Per node
          </button>
          {nodesExpanded && (
            <div className="balance-nodes">
              {balance.rows.map((row) => (
                <button
                  key={row.nodeId}
                  type="button"
                  className={`balance-row balance-node${selectedNodeId === row.nodeId ? ' sel' : ''}`}
                  onClick={() =>
                    onSelectNode(selectedNodeId === row.nodeId ? null : row.nodeId)
                  }
                >
                  <span className="label">{row.label}</span>
                  <span className="num in">{fmt(row.inflow)}</span>
                  <span className="num out">{fmt(row.outflow)}</span>
                  <span className={`num net ${row.net >= 0 ? 'pos' : 'neg'}`}>{fmt(row.net)}</span>
                </button>
              ))}
            </div>
          )}
          {/* Per group — a second breakdown of the SAME node totals, sliced by
              spatial membership. Shown only when the bundle has node groups.
              These rows aggregate member nodes; they are NOT included in the
              "All" total above (groups overlap and are subsets of the node
              set, so summing them in would double-count). */}
          {(bundle.nodeGroups ?? []).length > 0 && (
            <>
              <button
                type="button"
                className="balance-toggle"
                onClick={() => setGroupsExpanded((x) => !x)}
              >
                {groupsExpanded ? '▼' : '▶'} Per group
              </button>
              {groupsExpanded && (
                <div className="balance-nodes">
                  {(bundle.nodeGroups ?? []).map((g) => {
                    const b = groupBalance[g.id];
                    const net = b ? b.inflow - b.outflow : 0;
                    return (
                      <button
                        key={g.id}
                        type="button"
                        className={`balance-row balance-node balance-group-row${selectedGroupId === g.id ? ' sel' : ''}`}
                        onClick={() =>
                          onSelectGroup?.(selectedGroupId === g.id ? null : g.id)
                        }
                        title="Open group"
                      >
                        <span className="label">
                          <span className="group-dot" style={{ backgroundColor: g.color ?? 'var(--neutral)' }} />
                          {g.label || g.id}
                        </span>
                        <span className="num in">{b ? fmt(b.inflow) : '—'}</span>
                        <span className="num out">{b ? fmt(b.outflow) : '—'}</span>
                        <span className={`num net ${(b ? net : 0) >= 0 ? 'pos' : 'neg'}`}>
                          {b ? fmt(net) : '—'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      
      <KnobsSection
        bundle={bundle}
        knobs={bundle.knobs}
        canEdit={canEdit && !!onSetKnobs}
        paramValues={paramValues}
        tablesState={tablesState}
        onLoadTables={onLoadTables}
        onUpdateTableCell={onUpdateTableCell}
        onSetParam={onSetParam}
        onSetKnobs={onSetKnobs}
        onEdit={() => setEditingKnobs(true)}
      />

      {editingKnobs && onSetKnobs && (
        <KnobsEditModal
          bundle={bundle}
          knobs={bundle.knobs ?? []}
          paramValues={paramValues}
          tablesState={tablesState ?? null}
          onLoadTables={onLoadTables}
          onClose={() => setEditingKnobs(false)}
          onChange={onSetKnobs}
        />
      )}

      {/* ─── Legend ─────────────────────────────────────────────────── */}
      <section className="sidebar-section">
        <LegendEditor
          canEdit={canEdit && !!onSetLegend}
          initialEntries={legendEntries}
          onChange={onSetLegend}
        />
      </section>

      {/* ─── Checks (static graph lint) ───────────────────────────────── */}
      <LintSection graph={bundle.graph} nodeLabel={nodeLabel} />


      {editingMetric !== undefined && (
        <GlobalMetricsEditModal
          metric={editingMetric}
          bundle={bundle}
          onClose={() => setEditingMetric(undefined)}
          onSave={saveMetric}
          onDelete={editingMetric ? deleteMetric : undefined}
          keyTaken={(key) =>
            bundle.metrics.some((m) => m.key === key && m.key !== (editingMetric as MetricSpec | null)?.key)
          }
        />
      )}
    </aside>
  );
}

function MetricCard({
  spec,
  value,
  editable,
  onEdit,
}: {
  spec: MetricSpec;
  value: number;
  editable: boolean;
  onEdit: () => void;
}): JSX.Element {
  const v = value;
  const display = Number.isFinite(v) ? fmt(v) : '—';
  const arrow = spec.direction === 'up' ? '▲' : '▼';
  //const goodSign = spec.direction === 'up' ? v >= 0 : v <= 0;
  // For ratio metrics commonly in [0,1], show as a percentage.
  const isRatio = spec.unit === '%';
  const shown = isRatio && Number.isFinite(v) ? `${(v * 100).toFixed(0)}%` : display;
  return (
    <div
      className={`metric-card`}
      onDoubleClick={() => editable && onEdit()}
      title={editable ? 'Double-click to edit' : undefined}
    >
      <div className="metric-label">
        <span className="arrow">{arrow}</span>
        {spec.label}
      </div>
      <div className="metric-value">
        {shown}
        {!isRatio && spec.unit ? <span className="unit"> {spec.unit}</span> : null}
      </div>
    </div>
  );
}

function LegendEditor({
  canEdit,
  initialEntries,
  onChange,
}: {
  canEdit: boolean;
  initialEntries: LegendEntry[];
  onChange?: (entries: LegendEntry[]) => void;
}) {
  // Local copy drives the live edit UI; `onChange` propagates each edit up so
  // it persists through the adapter (every edit persists, same as node drags).
  // When not editing, the read view renders straight from `initialEntries`.
  const [entries, setEntries] = useState<LegendEntry[]>(initialEntries);
  const [isEditing, setIsEditing] = useState(false);

  // Enter edit mode seeded with the current (saved or derived) entries; leave
  // edit mode without forcing a re-save (each change already persisted live).
  const beginEdit = () => {
    setEntries(initialEntries);
    setIsEditing(true);
  };

  useEffect(() => {
    if (!canEdit) setIsEditing(false);
  }, [canEdit]);

  // Apply an edit locally + propagate. The adapter stores an empty list as
  // undefined so the sidebar falls back to the palette-derived default.
  const apply = (next: LegendEntry[]) => {
    setEntries(next);
    onChange?.(next);
  };

  const header = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
      <h3 style={{ margin: 0 }}>Legend</h3>
      {canEdit && (
        <button
          className={`btn-icon ${isEditing ? 'active' : ''}`}
          onClick={() => (isEditing ? setIsEditing(false) : beginEdit())}
          title={isEditing ? 'Done editing' : 'Edit legend'}
        >
          <PencilSimple size={14} />
        </button>
      )}
    </div>
  );

  if (!canEdit || !isEditing) {
    return (
      <>
        {header}
        <ul className="legend">
          {initialEntries.map((e, i) => (
            <li key={i}>
              <span className="swatch" style={{ background: e.color }} />
              {e.label}
            </li>
          ))}
        </ul>
      </>
    );
  }

  return (
    <>
      {header}
      <div className="legend-editor">
        {entries.map((e, i) => (
          <div key={i} className="legend-edit-row">
            <input
              type="color"
              value={e.color}
              className="legend-color-picker"
              onChange={(ev) => {
                const next = [...entries];
                next[i] = { ...e, color: ev.target.value };
                apply(next);
              }}
            />
            <input
              type="text"
              value={e.label}
              className="legend-input"
              onChange={(ev) => {
                const next = [...entries];
                next[i] = { ...e, label: ev.target.value };
                apply(next);
              }}
            />
            <button
              type="button"
              className="btn btn-mini"
              style={{ padding: '4px' }}
              onClick={() => apply(entries.filter((_, idx) => idx !== i))}
            >
              <X size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-mini"
          style={{ marginTop: 8 }}
          onClick={() => apply([...entries, { label: 'New', color: '#888888' }])}
        >
          <Plus size={12} style={{ marginRight: 4 }} /> Add legend
        </button>
      </div>
    </>
  );
}

function fmt(n: number): string {
  if (Math.abs(n) >= 10000) return (n / 1000).toFixed(1) + 'k';
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1);
}

// ─── Knobs section ──────────────────────────────────────────────────────────

const KNOBS_PREVIEW_COUNT = 6;

/** Resolve a graph knob's underlying ParamSpec by walking the bundle. The path
 *  grammar is `<nodeId>.<key>` / `e:<edgeId>.<key>`; a node's params may live
 *  in `node.params`, `node.gate.params`, or `node.source.distParams` (all map
 *  to the same `params.nodes[id][key]` namespace, so keys are unique per node).
 *  Returns `{spec, ownerLabel}` or `null` when the node/edge/param was deleted
 *  (the row then renders a stale state with an unpin affordance). */
function resolveGraphKnob(
  bundle: ScenarioBundle,
  path: string,
): { spec: ParamSpec; ownerLabel: string; binding: CellBinding | null } | null {
  const nodeLabelOf = (id: string): string =>
    bundle.graph.nodes.find((n) => n.id === id)?.label ?? id;
  const edgeLabelOf = (id: string): string => {
    const e = bundle.graph.edges.find((x) => x.id === id);
    if (!e) return id;
    return `${nodeLabelOf(e.source)} → ${nodeLabelOf(e.target)}`;
  };
  if (path.startsWith('e:')) {
    const rest = path.slice(2);
    const dot = rest.indexOf('.');
    if (dot < 0) return null;
    const id = rest.slice(0, dot);
    const key = rest.slice(dot + 1);
    const edge = bundle.graph.edges.find((e) => e.id === id);
    if (!edge) return null;
    const spec = edge.params.find((p) => p.key === key);
    if (spec) return { spec, ownerLabel: edgeLabelOf(id), binding: spec.binding ?? null };
    if (key === 'weight') {
      // The implicit weight param (e:<id>.weight) — no ParamSpec of its own;
      // synthesize one so knob pins + binding display work. Range [0, 1] is
      // the authored fallback's domain; a bound value may exceed it (fan-out
      // normalises) and only clamps for the slider display.
      return {
        spec: { key: 'weight', label: 'weight', value: edge.weight, min: 0, max: 1 },
        ownerLabel: edgeLabelOf(id),
        binding: edge.weightBinding ?? null,
      };
    }
    return null;
  }
  const dot = path.indexOf('.');
  if (dot < 0) return null;
  const id = path.slice(0, dot);
  const key = path.slice(dot + 1);
  const node = bundle.graph.nodes.find((n) => n.id === id);
  if (!node) return null;
  const lists: ParamSpec[][] = [node.params];
  if (node.gate?.params) lists.push(node.gate.params);
  if (node.source?.distParams) lists.push(node.source.distParams);
  for (const list of lists) {
    const spec = list.find((p) => p.key === key);
    if (spec) return { spec, ownerLabel: nodeLabelOf(id), binding: spec.binding ?? null };
  }
  return null;
}

/** Read a db knob's current cell value from the tables snapshot. Returns
 *  `undefined` when the table isn't loaded or the row/column is gone.
 *  Row-id comparison is loose over numbers vs numeric strings — the app mints
 *  numeric ids while storages may return them as strings. */
function readDbCellValue(
  tables: KnobTableSnapshot[] | null | undefined,
  pin: DbKnobPin,
): number | undefined {
  const t = tables?.find((x) => x.name === pin.table);
  const row = t?.rows.find((r) => String(r.id) === String(pin.rowId));
  const v = row?.cells[pin.column];
  return typeof v === 'number' ? v : undefined;
}

function KnobsSection({
  bundle,
  knobs,
  canEdit,
  paramValues,
  tablesState,
  onLoadTables,
  onUpdateTableCell,
  onSetParam,
  onSetKnobs,
  onEdit,
}: {
  bundle: ScenarioBundle;
  knobs?: KnobPin[];
  canEdit: boolean;
  paramValues?: Record<string, number>;
  tablesState?: KnobTableSnapshot[] | null;
  onLoadTables?: () => Promise<void>;
  onUpdateTableCell?: (table: string, rowId: number | string, column: string, value: number) => Promise<void>;
  onSetParam?: (path: string, value: number) => void;
  onSetKnobs?: (knobs: KnobPin[]) => void;
  onEdit: () => void;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  // Sort mode: shows the drag handle + unpin button on every knob row (off =
  // the rows are read/adjust only). Off by default so the list stays clean.
  const [sortMode, setSortMode] = useState(false);
  // If any db knob exists but tables aren't loaded, pull them once so the
  // sliders can render current values. Local mode (no onLoadTables) just shows
  // the configured range without a live value.
  const hasDb = (knobs ?? []).some((k) => k.type === 'db');
  const loadedRef = useRef(false);
  useEffect(() => {
    if (hasDb && tablesState == null && onLoadTables && !loadedRef.current) {
      loadedRef.current = true;
      void onLoadTables();
    }
  }, [hasDb, tablesState, onLoadTables]);

  const all = knobs ?? [];
  const visible = expanded ? all : all.slice(0, KNOBS_PREVIEW_COUNT);
  const hiddenCount = all.length - visible.length;

  const removePin = (id: string) => onSetKnobs?.(all.filter((k) => k.id !== id));

  // ── Drag-sort (native HTML5 DnD; reorder a local copy, commit on drop). ──
  const dragId = useRef<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const reorder = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    const fromIdx = all.findIndex((k) => k.id === fromId);
    const toIdx = all.findIndex((k) => k.id === toId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = all.slice();
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    onSetKnobs?.(next);
  };

  return (
    <section className="sidebar-section knobs-section">
      <div className="knobs-header">
        <h3>Knobs</h3>
        {canEdit && (
          <span>
            <button
              type="button"
              className={`btn-icon${sortMode ? ' on' : ''}`}
              onClick={() => setSortMode((s) => !s)}
              title={sortMode ? 'Exit sort mode' : 'Sort mode (reorder + unpin)'}
              aria-pressed={sortMode}
            >
              <ArrowsDownUp size={14} />
            </button>
            <button
              type="button"
              className="btn-icon"
              onClick={onEdit}
              title="Edit pinned knobs"
            >
              <PencilSimple size={14} />
            </button>
          </span>
        )}
      </div>

      {all.length === 0 ? (
        <p className="knobs-empty">
          Pin knobs from the graph or project tables.
          {canEdit && <> Click the pencil to add some.</>}
        </p>
      ) : (
        <div className="knobs-list">
          {visible.map((pin) => (
            <KnobRow
              key={pin.id}
              bundle={bundle}
              pin={pin}
              canEdit={canEdit}
              canSort={canEdit && sortMode}
              paramValues={paramValues}
              tablesState={tablesState}
              onSetParam={onSetParam}
              onUpdateTableCell={onUpdateTableCell}
              onRemove={() => removePin(pin.id)}
              dragging={dragId.current === pin.id}
              dragOver={overId === pin.id && dragId.current !== null && dragId.current !== pin.id}
              onDragStart={() => {
                dragId.current = pin.id;
              }}
              onDragEnd={() => {
                dragId.current = null;
                setOverId(null);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (dragId.current && dragId.current !== pin.id) setOverId(pin.id);
              }}
              onDrop={() => {
                if (dragId.current) reorder(dragId.current, pin.id);
                dragId.current = null;
                setOverId(null);
              }}
            />
          ))}
          {hiddenCount > 0 && (
            <button
              type="button"
              className="knobs-show-more"
              onClick={() => setExpanded(true)}
            >
              Show {hiddenCount} more
            </button>
          )}
          {expanded && all.length > KNOBS_PREVIEW_COUNT && (
            <button
              type="button"
              className="knobs-show-more"
              onClick={() => setExpanded(false)}
            >
              Show less
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function KnobRow({
  bundle,
  pin,
  canEdit,
  canSort,
  paramValues,
  tablesState,
  onSetParam,
  onUpdateTableCell,
  onRemove,
  dragging,
  dragOver,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: {
  bundle: ScenarioBundle;
  pin: KnobPin;
  canEdit: boolean;
  canSort: boolean;
  paramValues?: Record<string, number>;
  tablesState?: KnobTableSnapshot[] | null;
  onSetParam?: (path: string, value: number) => void;
  onUpdateTableCell?: (table: string, rowId: number | string, column: string, value: number) => Promise<void>;
  onRemove: () => void;
  dragging: boolean;
  dragOver: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
}): JSX.Element {
  // Graph knob: resolve live range + label from the underlying ParamSpec.
  if (pin.type === 'graph') {
    const resolved = resolveGraphKnob(bundle, pin.path);
    if (!resolved) {
      return (
        <StaleKnobRow
          canSort={canSort}
          dragging={dragging}
          dragOver={dragOver}
          label={pin.label ?? pin.path}
          note="Missing — node or param was deleted"
          onRemove={onRemove}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragOver={onDragOver}
          onDrop={onDrop}
        />
      );
    }
    const { spec, ownerLabel, binding } = resolved;
    // Cell-bound knob: the DB cell is the source of truth — the slider reads
    // it (clamped to the spec range for display) and writes PATCH the cell
    // (debounced, like db knobs) + setParam for immediate engine effect. A
    // broken binding keeps the ordinary graph-knob behaviour + a ⚠ note.
    const lookup = binding && tablesState ? lookupBoundCell(tablesState, binding) : null;
    const broken = !!binding && (!lookup || lookup.rowId === null || lookup.value === null);
    const clamped = lookup?.value != null
      ? Math.min(spec.max, Math.max(spec.min, lookup.value))
      : null;
    const value = broken || clamped == null
      ? (paramValues?.[pin.path] ?? spec.value)
      : clamped;
    const step = spec.step ?? ((spec.max - spec.min) / 100 || 1);
    return (
      <KnobRowShell
        canEdit={canEdit}
        canSort={canSort}
        dragging={dragging}
        dragOver={dragOver}
        label={pin.label ?? spec.label}
        sublabel={binding ? `${broken ? '⚠ ' : ''}${bindingLabel(binding)} · ${ownerLabel}` : ownerLabel}
        min={spec.min}
        max={spec.max}
        step={step}
        value={value}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        onDrop={onDrop}
        // Graph writes are immediate + high-frequency (the engine reads the
        // new value on the next tick); a bound knob ALSO PATCHes its cell
        // (debounced — a network write, like db knobs).
        onChange={(v) => {
          onSetParam?.(pin.path, v);
          const rowId = lookup?.rowId;
          if (binding && rowId !== null && rowId !== undefined && onUpdateTableCell) {
            const target: string | number = rowId;
            scheduleKnobPatch(pin.id, () => {
              void onUpdateTableCell(binding.table, target, binding.column, v);
            });
          }
        }}
        onRemove={onRemove}
      />
    );
  }

  // DB knob: range is on the pin; current value from the loaded table.
  const liveValue = readDbCellValue(tablesState, pin);
  const step = pin.step ?? ((pin.max - pin.min) / 100 || 1);
  const tablesLoaded = !!tablesState?.some((t) => t.name === pin.table);
  if (!tablesLoaded) {
    return (
      <StaleKnobRow
        canSort={canSort}
        dragging={dragging}
        dragOver={dragOver}
        label={pin.label}
        note="Loading table…"
        onRemove={onRemove}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        onDrop={onDrop}
      />
    );
  }
  return (
    <KnobRowShell
      canEdit={canEdit}
      canSort={canSort}
      dragging={dragging}
      dragOver={dragOver}
      label={pin.label}
      sublabel={`${pin.table}.${pin.column} #${pin.rowId}`}
      min={pin.min}
      max={pin.max}
      step={step}
      value={liveValue ?? pin.min}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      // DB writes are a network PATCH — debounce so a drag doesn't flood.
      onChange={(v) => {
        if (!onUpdateTableCell) return;
        scheduleKnobPatch(pin.id, () => {
          void onUpdateTableCell(pin.table, pin.rowId, pin.column, v);
        });
      }}
      onRemove={onRemove}
    />
  );
}

/** Per-pin debounce timer map for db-knob PATCH commits. Module-level so it
 *  survives re-renders; keyed by pin id. */
const knobPatchTimers: Record<string, number> = {};
function scheduleKnobPatch(id: string, fn: () => void): void {
  if (knobPatchTimers[id]) window.clearTimeout(knobPatchTimers[id]);
  knobPatchTimers[id] = window.setTimeout(fn, 200);
}

/** The shared slider shell for both knob kinds. Owns the optimistic display
 *  value: graph params only reflect on the next tick and db cells only after
 *  the PATCH round-trips, so the slider must track the cursor locally and
 *  resync when the external value catches up. */
function KnobRowShell({
  canEdit,
  canSort,
  dragging,
  dragOver,
  label,
  sublabel,
  min,
  max,
  step,
  value: externalValue,
  onChange,
  onRemove,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: {
  canEdit: boolean;
  canSort: boolean;
  dragging: boolean;
  dragOver: boolean;
  label: string;
  sublabel?: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
  onRemove: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
}): JSX.Element {
  const [optimistic, setOptimistic] = useState<number | null>(null);
  const [handleHovered, setHandleHovered] = useState(false);
  // Pointer-down guard so an in-flight tick (graph) or PATCH round-trip (db)
  // doesn't yank the slider out from under the cursor mid-drag.
  const draggingRef = useRef(false);
  // Safety net: if the pointerup lands off-element (no pointer capture), the
  // input's own handler never fires and `draggingRef` would stick — pinning
  // the optimistic value forever. A window listener clears it regardless.
  useEffect(() => {
    const onUp = () => {
      if (draggingRef.current) {
        draggingRef.current = false;
        setOptimistic((cur) => (cur !== null && Math.abs(externalValue - cur) <= step ? null : cur));
      }
    };
    window.addEventListener('pointerup', onUp);
    return () => window.removeEventListener('pointerup', onUp);
  }, [externalValue, step]);

  // Resync: drop the optimistic override once the external value catches up
  // (commit landed) — but never while the pointer is still down.
  useEffect(() => {
    if (optimistic === null || draggingRef.current) return;
    if (Math.abs(externalValue - optimistic) <= step) {
      setOptimistic(null);
    }
  }, [externalValue, optimistic, step]);

  const displayed = Math.max(min, Math.min(max, optimistic ?? externalValue));

  return (
    <div
      className={`knob-row${dragging ? ' dragging' : ''}${dragOver ? ' drag-over' : ''}`}
      draggable={canSort && handleHovered}
      onDragStart={(e) => {
        if (!handleHovered) {
          e.preventDefault();
          return;
        }
        if (canSort) onDragStart();
      }}
      onDragEnd={canSort ? onDragEnd : undefined}
      onDragOver={canSort ? onDragOver : undefined}
      onDrop={canSort ? onDrop : undefined}
    >
      {canSort && (
        <span 
          className="knob-drag-handle" 
          title="Drag to reorder"
          onPointerEnter={() => setHandleHovered(true)}
          onPointerLeave={() => setHandleHovered(false)}
        >
          <DotsSixVertical size={14} />
        </span>
      )}
      <div className="knob-row-body">
        <div className="knob-row-head">
          <span className="knob-row-label" title={sublabel}>{label}</span>
          <span className="knob-row-value">{fmt(displayed)}</span>
        </div>
        <input
          type="range"
          className="knob-slider"
          min={min}
          max={max}
          step={step}
          value={displayed}
          disabled={!canEdit}
          onChange={(e) => {
            const v = Number(e.target.value);
            setOptimistic(v);
            onChange(v);
          }}
          onPointerDown={() => { draggingRef.current = true; }}
          onPointerUp={() => {
            draggingRef.current = false;
            // Nudge the resync effect: if external already matches, clear now.
            setOptimistic((cur) => (cur !== null && Math.abs(externalValue - cur) <= step ? null : cur));
          }}
        />
        <span className="param-range">[{fmt(min)}, {fmt(max)}]</span>
      </div>
      {canSort && (
        <button
          type="button"
          className="btn-icon-mini knob-remove"
          onClick={onRemove}
          title="Unpin"
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}

/** Placeholder row for a knob whose backing node/param/table is missing. Keeps
 *  the drag handle + unpin affordance so the user can clean up. */
function StaleKnobRow({
  canSort,
  dragging,
  dragOver,
  label,
  note,
  onRemove,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: {
  canSort: boolean;
  dragging: boolean;
  dragOver: boolean;
  label: string;
  note: string;
  onRemove: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: () => void;
}): JSX.Element {
  const [handleHovered, setHandleHovered] = useState(false);
  return (
    <div
      className={`knob-row stale${dragging ? ' dragging' : ''}${dragOver ? ' drag-over' : ''}`}
      draggable={canSort && handleHovered}
      onDragStart={(e) => {
        if (!handleHovered) {
          e.preventDefault();
          return;
        }
        if (canSort) onDragStart();
      }}
      onDragEnd={canSort ? onDragEnd : undefined}
      onDragOver={canSort ? onDragOver : undefined}
      onDrop={canSort ? onDrop : undefined}
    >
      {canSort && (
        <span 
          className="knob-drag-handle" 
          title="Drag to reorder"
          onPointerEnter={() => setHandleHovered(true)}
          onPointerLeave={() => setHandleHovered(false)}
        >
          <DotsSixVertical size={14} />
        </span>
      )}
      <div className="knob-row-body">
        <span className="knob-row-label">{label}</span>
        <span className="knob-row-note">{note}</span>
      </div>
      {canSort && (
        <button
          type="button"
          className="btn-icon-mini knob-remove"
          onClick={onRemove}
          title="Unpin"
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}

/** Static graph checks (the unit-consistency lint) — rendered only when the
 *  lint has something to say. Pure computation memoized on the graph. */
function LintSection({
  graph,
  nodeLabel,
}: {
  graph: ScenarioBundle['graph'];
  nodeLabel: (id: string) => string;
}): JSX.Element | null {
  const warnings = useMemo(() => graphLint(graph), [graph]);
  if (warnings.length === 0) return null;
  return (
    <section className="sidebar-section">
      <h3>Checks</h3>
      <ul className="lint-list">
        {warnings.map((w) => (
          <li key={w.nodeId} className="lint-item" title={w.message}>
            <span className="lint-node">{nodeLabel(w.nodeId)}</span>
            <span className="lint-message">{w.message}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
